import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setUnauthorizedHandler } from '@/lib/api';
import { isMfaPending, type AuthUser, type LoginInput, type LoginResult } from './types';

export const AUTH_ME_KEY = ['auth', 'me'] as const;

interface AuthContextValue {
  /** 未ログインは null。確認中は undefined */
  user: AuthUser | null | undefined;
  isLoading: boolean;
  /** ID・パスワードでログイン。2段階認証が必要な場合は確認待ち（MfaPendingResult）を返し、ログイン状態にはしない */
  login: (input: LoginInput) => Promise<LoginResult>;
  /** 2段階認証の確認が完了したユーザーでログイン状態にする */
  completeLogin: (user: AuthUser) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** /api/auth/me を取得。401 は「未ログイン」として null を返す */
async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await api.get<AuthUser | { user: AuthUser }>('/auth/me', undefined, { skipUnauthorizedRedirect: true });
    return unwrapUser(res);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

function isAuthMeKey(key: readonly unknown[]): boolean {
  return key.length === AUTH_ME_KEY.length && AUTH_ME_KEY.every((k, i) => key[i] === k);
}

function unwrapUser(res: AuthUser | { user: AuthUser }): AuthUser {
  return 'user' in res ? res.user : res;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: AUTH_ME_KEY, queryFn: fetchMe, staleTime: 5 * 60_000 });

  // 401 を受けたら未ログイン状態にする → RequireAuth がログイン画面へ遷移させる
  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.setQueryData(AUTH_ME_KEY, null);
    });
    return () => setUnauthorizedHandler(null);
  }, [qc]);

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) =>
      api.post<AuthUser | { user: AuthUser } | LoginResult>('/auth/login', input, { skipUnauthorizedRedirect: true }),
  });

  const completeLogin = useCallback(
    (user: AuthUser) => {
      // 前のユーザーのキャッシュを破棄してからログインユーザーを設定する。
      // ['auth','me'] は破棄せずに更新する（購読中の AuthProvider に変更を通知するため。
      // qc.clear() で破棄すると、ミューテーションを伴わない 2段階認証の完了時に画面へ反映されない）
      qc.removeQueries({ predicate: (q) => !isAuthMeKey(q.queryKey) });
      qc.setQueryData(AUTH_ME_KEY, user);
    },
    [qc],
  );

  const login = useCallback(
    async (input: LoginInput): Promise<LoginResult> => {
      const res = await loginMutation.mutateAsync(input);
      if (isMfaPending(res as LoginResult)) return res as LoginResult;
      const user = unwrapUser(res as AuthUser | { user: AuthUser });
      completeLogin(user);
      return user;
    },
    [loginMutation, completeLogin],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipUnauthorizedRedirect: true });
    } finally {
      // 先に未ログイン状態にしてから他のキャッシュを破棄する。
      // qc.clear() で ['auth','me'] ごと破棄すると、購読中の observer が旧ユーザーを保持したまま
      // ログイン画面 → '/' へ再遷移し、401 で「データを取得できませんでした」になる（障害 BUG-001）。
      qc.setQueryData(AUTH_ME_KEY, null);
      qc.removeQueries({ predicate: (q) => !isAuthMeKey(q.queryKey) });
    }
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({ user: me.data, isLoading: me.isPending, login, completeLogin, logout }),
    [me.data, me.isPending, login, completeLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth は AuthProvider の内側で使用してください');
  return ctx;
}

/** ログイン済みユーザー（RequireAuth の内側でのみ使用） */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser は RequireAuth の内側で使用してください');
  return user;
}
